import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Users, PenTool, Loader2, BookOpen, Play, Globe } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AIContentGenerator from '@/components/AIContentGenerator';
import AppShell from '@/components/AppShell';
import UnitActionBar from '@/components/UnitActionBar';
import PrintHeader from '@/components/PrintHeader';
import PrintReadyMarker from '@/components/PrintReadyMarker';
import {
  StandardsTab, LessonsTab, VocabularyTab, IdentificationsTab, VideosTab, GeographyTab,
} from '@/components/unitDetail/tabs';
import { useAuth } from '@/context/AuthContext';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnitDetail() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { isStudent } = useAuth();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [generatingEQ, setGeneratingEQ] = useState(false);
  const [generatingStandards, setGeneratingStandards] = useState(false);
  const [unitVideos, setUnitVideos] = useState([]);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [pinned, setPinned] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  const fetchUnit = useCallback(async () => {
    try {
      setLoading(true);
      const [unitRes, videoRes, pinRes] = await Promise.all([
        axios.get(`${API}/units/${unitId}`),
        axios.get(`${API}/videos/unit/${unitId}`),
        axios.get(`${API}/collections/pinned`).catch(() => ({ data: { pinned_unit_ids: [] } })),
      ]);
      setUnit(unitRes.data);
      setUnitVideos(videoRes.data);
      setPinned((pinRes.data?.pinned_unit_ids || []).includes(unitId));
    } catch {
      toast.error('Failed to load unit details');
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  useEffect(() => { fetchUnit(); }, [fetchUnit]);

  const togglePin = async () => {
    try {
      setPinLoading(true);
      const res = await axios.post(`${API}/collections/toggle/${unitId}`);
      const isNowPinned = (res.data?.pinned_unit_ids || []).includes(unitId);
      setPinned(isNowPinned);
      toast.success(isNowPinned ? 'Added to your collection' : 'Removed from collection');
    } catch {
      toast.error('Could not update collection');
    } finally {
      setPinLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/units/${unitId}`);
      toast.success('Unit deleted successfully');
      navigate(`/dashboard/${unit.grade_level}`);
    } catch {
      toast.error('Failed to delete unit');
    }
  };

  const handleGenerateEssentialQuestions = async () => {
    try {
      setGeneratingEQ(true);
      await axios.post(`${API}/units/${unitId}/generate-essential-questions`);
      toast.success('Essential questions generated!');
      fetchUnit();
    } catch {
      toast.error('Failed to generate essential questions');
    } finally {
      setGeneratingEQ(false);
    }
  };

  const handleEnhanceStandards = async () => {
    try {
      setGeneratingStandards(true);
      await axios.post(`${API}/units/${unitId}/enhance-standards`);
      toast.success('Standards enhanced!');
      fetchUnit();
    } catch {
      toast.error('Failed to enhance standards');
    } finally {
      setGeneratingStandards(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#14213D] mx-auto" />
            <p className="mt-4 text-sm text-[#5A6577]">Loading unit...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!unit) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <p className="text-sm text-[#5A6577]">Unit not found</p>
        </div>
      </AppShell>
    );
  }

  const accent = gradeAccent(unit.grade_level);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print:max-w-none print:px-0 print:py-2">
        <PrintReadyMarker ready={!loading && !!unit} waitForImages />
        <PrintHeader
          resourceType="Unit Reference"
          resourceTitle={unit?.title}
          grade={unit?.grade_level}
          unitNumber={unit?.unit_number}
        />
        <div className="print:hidden">
          <UnitActionBar
            unit={unit}
            unitId={unitId}
            pinned={pinned}
            pinLoading={pinLoading}
            showAIGenerator={showAIGenerator}
            onTogglePin={togglePin}
            onToggleAIGenerator={() => setShowAIGenerator(!showAIGenerator)}
            onPrint={() => window.print()}
            onDelete={handleDelete}
          />
        </div>

        {showAIGenerator && (
          <AIContentGenerator
            unit={unit}
            onClose={() => setShowAIGenerator(false)}
            onContentGenerated={fetchUnit}
          />
        )}

        <div className="mb-8 print-friendly" data-testid="unit-header">
          <p className="text-[11px] uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: accent.hex }}>
            Grade {unit.grade_level} &middot; {accent.subject} &middot; Unit {unit.unit_number}
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#14213D] tracking-tight mb-4 leading-tight break-words">
            {unit.title}
          </h1>
          <p className="text-sm sm:text-base text-[#5A6577] leading-relaxed max-w-3xl">{unit.description}</p>
        </div>

        <Tabs defaultValue="standards" className="space-y-6">
          <div className="no-print -mx-4 sm:mx-0 overflow-x-auto">
            <TabsList className="bg-[#F9F8F4] w-max min-w-full sm:w-auto sm:min-w-0 mx-4 sm:mx-0">
              <TabsTrigger value="standards" data-testid="standards-tab">
                <FileText className="h-4 w-4 mr-2" />
                Standards
              </TabsTrigger>
              <TabsTrigger value="lessons" data-testid="lessons-tab">
                <BookOpen className="h-4 w-4 mr-2" />
                Lesson Plans
              </TabsTrigger>
              <TabsTrigger value="vocabulary" data-testid="vocabulary-tab">
                <PenTool className="h-4 w-4 mr-2" />
                Vocabulary
              </TabsTrigger>
              <TabsTrigger value="identifications" data-testid="identifications-tab">
                <Users className="h-4 w-4 mr-2" />
                Identifications
              </TabsTrigger>
              {unit.geography_connections && (
                <TabsTrigger value="geography" data-testid="geography-tab-trigger">
                  <Globe className="h-4 w-4 mr-2" />
                  Geography
                </TabsTrigger>
              )}
              {unitVideos.length > 0 && (
                <TabsTrigger value="videos" data-testid="videos-tab">
                  <Play className="h-4 w-4 mr-2" />
                  Videos ({unitVideos.length})
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="standards">
            <StandardsTab
              unit={unit}
              generatingEQ={generatingEQ}
              generatingStandards={generatingStandards}
              onGenerateEssentialQuestions={handleGenerateEssentialQuestions}
              onEnhanceStandards={handleEnhanceStandards}
            />
          </TabsContent>

          <TabsContent value="lessons">
            <LessonsTab unit={unit} />
          </TabsContent>

          <TabsContent value="vocabulary">
            <VocabularyTab unit={unit} isStudent={isStudent} />
          </TabsContent>

          <TabsContent value="identifications">
            <IdentificationsTab unit={unit} isStudent={isStudent} />
          </TabsContent>

          {unit.geography_connections && (
            <TabsContent value="geography">
              <GeographyTab unit={unit} />
            </TabsContent>
          )}

          {unitVideos.length > 0 && (
            <TabsContent value="videos">
              <VideosTab
                unitVideos={unitVideos}
                playingVideoId={playingVideoId}
                onTogglePlay={(id) => setPlayingVideoId(playingVideoId === id ? null : id)}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </AppShell>
  );
}
